// @flow

import { v4 } from 'uuid';
import React, { Fragment } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { Section, SectionContent, Wrapper, EmptyContent } from '@riseart/layout';
import { LazyloadPlaceholder } from '@riseart/common';
import { useQuery } from 'shared_services/apollo/useQuery';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { ArtistEventList } from 'shared_components/artist/sections/event/List';
import { ArtistArticlesList } from 'shared_components/artist/sections/article/List';
import LIST_NEWS from 'shared_data/queries/artist/listNews.graphql';

type Props = { artistId: ?number };

const LAZYLOAD_EVENT_CONFIG = {
  once: true,
  height: 260,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={260} />,
};
const LAZYLOAD_ARTICLE_CONFIG = {
  once: true,
  height: 175,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={175} />,
};
const PRELOADER_SUBSCRIBER_ID = v4();

/**
 * ArtistNews
 */
export function ArtistNews({ artistId }: Props): Node {
  const { data: dataNews, loading: loadingNews } = useQuery(LIST_NEWS, {
    ssr: false,
    skip: !artistId,
    variables: {
      artistId,
      pageNews: 1,
      itemsNews: 3,
      pageArticles: 1,
      itemsArticles: 6,
    },
  });
  const {
    listEvents: { items } = { items: null },
    listArticles: { items: articlesItems } = { items: null },
  } = dataNews || {};
  const totalItems = (items && items.length) || 0;
  const totalArticles = (articlesItems && articlesItems.length) || 0;

  return (
    <Fragment>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_ID} attach={loadingNews} />
      {!loadingNews && !totalItems && !totalArticles ? (
        <Section>
          <Wrapper>
            <EmptyContent>
              <FormattedMessage id="components.artist.noNews">
                {(text: string) => text}
              </FormattedMessage>
            </EmptyContent>
          </Wrapper>
        </Section>
      ) : null}
      {totalItems ? (
        <Section>
          <Wrapper
            sizes={{
              xs: 'wide',
              sm: 'wide',
              md: 'wide',
              lg: 'narrow',
              xl: 'narrow',
            }}
          >
            <SectionContent
              nodeType="div"
              title={
                <FormattedMessage id="components.artist.events">
                  {(title: string) => title}
                </FormattedMessage>
              }
              headingProps={{ tag: 'h2', align: 'left' }}
            >
              <ArtistEventList items={items} lazyloadConfig={LAZYLOAD_EVENT_CONFIG} />
            </SectionContent>
          </Wrapper>
        </Section>
      ) : null}
      {totalArticles ? (
        <Section>
          <Wrapper
            sizes={{
              xs: 'wide',
              sm: 'wide',
              md: 'wide',
              lg: 'narrow',
              xl: 'narrow',
            }}
          >
            <SectionContent
              nodeType="div"
              title={
                <FormattedMessage id="components.artist.featuredIn">
                  {(title: string) => title}
                </FormattedMessage>
              }
              headingProps={{ tag: 'h2', align: 'left' }}
            >
              <ArtistArticlesList items={articlesItems} lazyloadConfig={LAZYLOAD_ARTICLE_CONFIG} />
            </SectionContent>
          </Wrapper>
        </Section>
      ) : null}
    </Fragment>
  );
}
